// MIRRORED FILE — twin of src/utils/clientInvoiceSummary.js (line-for-line).
// CRA's ModuleScopePlugin forbids importing from the repo's src/, so the copy is the only way
// the page can use the rule. The pair is registered in `mirroredUtilParity.test.js` (PAIRS).
//
// The ONE owner of "what do these client-invoice lines sum to": quoted total, cost, margin,
// markup, discount, charges and grand total. Pure. The server stores it and the page previews
// it; nothing else re-sums these figures.
import { num } from './invoiceLineMath';
import { clampDiscountPercent } from './pricing';

// CO-03 — a margin past this is flagged, never clamped or hidden.
export const MARGIN_SANITY_BOUND_PCT = 300;

const money = (v: any): number => num(v) ?? 0;

export interface SummaryCharge {
  label?: string;
  amount?: any;
}

/** D56: discount = subtotal x pct / 100; charges = their own sum (a negative is a credit); grand = subtotal - discount + charges. */
export function totalsFromSubtotal(
  subtotal: number,
  discountPercent: any,
  charges: ReadonlyArray<SummaryCharge> | null | undefined
) {
  const pct = clampDiscountPercent(discountPercent);
  const discountAmount = Number((subtotal * (pct / 100)).toFixed(2));
  const chargesTotal = Number(
    (Array.isArray(charges) ? charges : []).reduce((sum, c) => sum + money(c?.amount), 0).toFixed(2)
  );
  const invoiceGrandTotal = Number((subtotal - discountAmount + chargesTotal).toFixed(2));
  return { discountAmount, chargesTotal, invoiceGrandTotal };
}

export interface SummaryOptions {
  discountPercent?: any;
  charges?: ReadonlyArray<SummaryCharge> | null;
  /** D96.5: a zero-line invoice's typed total (a number, zero or more); with lines, the lines decide. */
  headerTotal?: number | null;
}

/** Margin and markup need real cost data: with none they are null, never a 100% margin. */
export function buildClientInvoiceSummary(items: ReadonlyArray<any> | null | undefined, options: SummaryOptions = {}) {
  const lines: any[] = Array.isArray(items) ? (items as any[]) : [];
  const { discountPercent = 0, charges = [], headerTotal = null } = options || {};
  const sum = (field: string) => lines.reduce((total, item) => total + money(item?.[field]), 0);
  const quotedTotal = lines.length === 0 && headerTotal !== null ? headerTotal : sum('invoiceLineTotal');
  const actualCostTotal = sum('actualLineCost');
  const markupValueTotal = sum('markupAmount');
  // BUSINESS_RULES#client-invoice-line-vat — a vessel is never charged VAT.
  const vatTotal = 0;
  const { discountAmount, chargesTotal, invoiceGrandTotal } = totalsFromSubtotal(quotedTotal, discountPercent, charges);
  const profitVariance = Number((quotedTotal - actualCostTotal).toFixed(2));
  const hasCostData = lines.some((item) => money(item?.actualLineCost) > 0);
  const profitMarginPct =
    hasCostData && quotedTotal > 0 ? Number(((profitVariance / quotedTotal) * 100).toFixed(2)) : null;
  const markupPctVsCost =
    hasCostData && actualCostTotal > 0 ? Number(((profitVariance / actualCostTotal) * 100).toFixed(2)) : null;
  const marginSanityWarning =
    profitMarginPct != null && Math.abs(profitMarginPct) > MARGIN_SANITY_BOUND_PCT
      ? `Margin (${profitMarginPct}%) is far outside a normal range — verify the PO/vendor invoice match before relying on this figure.`
      : null;

  return {
    quotedTotal: Number(quotedTotal.toFixed(2)),
    actualCostTotal: Number(actualCostTotal.toFixed(2)),
    markupValueTotal: Number(markupValueTotal.toFixed(2)),
    vatTotal: Number(vatTotal.toFixed(2)),
    discountAmount,
    chargesTotal,
    profitVariance,
    profitMarginPct,
    markupPctVsCost,
    marginSanityWarning,
    invoiceGrandTotal,
    discrepancyCount: lines.filter((item) => String(item?.discrepancyAlert || '').trim()).length,
    returnCount: lines.filter((item) => item?.returnAction !== 'none' && Number(item?.returnQuantity) > 0).length,
    packingCount: lines.length,
    matchedPoCount: lines.filter((item) => Array.isArray(item?.poMatches) && item.poMatches.length).length,
  };
}
